import { Box, Card, Grid, Stack, Typography } from "@mui/material";
import { Dispatch, FC, useMemo, useState } from "react";
import { OdResourcePageProps } from "@/types/resource";
import OwnershipPieChart from "@/components/securefiles/OwnershipPieChart";
import { Content } from "@/components/content/Content";
import { ClassificationRuleAmbinetDataCotnext } from "@/components/securefiles/ClassificationRuleAmbientDataProvider";
import { FileCatalogSearchInput, FileInstanceOptions, FileTagResponseDto, PrimaryClassification } from "@/gen/api/securefile";
import FileCatalogSearchRegion from "@/components/securefiles/FileCatalogSearchRegion";
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "@/components/securefiles/FileCataAmbientDataProvider";
import { FileCataSearchContext, FileCataSearchStates, osfCatalogRes, pfsCatalogRes } from "@/components/securefiles/FileCataSearchContextProvider";
import { DataOwnerGroupResponseDto, SecondaryClassificationResponseDto } from "@/gen/api/governance";


const OwnershipStatisPage: FC<OdResourcePageProps> = ({
  options, location,
}) => {
  const filterFromRoute : FileCatalogSearchInput|undefined = (location?.state ?? {} as any).filter
  
  const [states, setStates] = useState({
    filters: {
      name: null,
      tags: [],
      primaryClassification: filterFromRoute?.primaryClassifications ?? [],
      secondClassification: [],
      documentTypes: [],
      infoTypes: [],
      fileInstanceOptions: FileInstanceOptions.All,
      ownerGroup: [],
    },
    currRes: '',
  } as FileCataSearchStates)

  const searchFilters = useMemo(() => {
    const filters = states.filters
    const f : FileCatalogSearchInput = {
      term: filters.name,
      tags: filters.tags.map(t => t.tagId!),
      primaryClassifications: filters.primaryClassification.map(x => x == null ? (x as any) : Number(x)),
      secondaryClassifications: filters.secondClassification.map(x => x?.secondaryClassificationId as any),
      documentTypes: filters.documentTypes,
      infoTypes: filters.infoTypes,
      fileInstanceOptions: filters.fileInstanceOptions,
      ownerMatchIds: filters.ownerGroup.map(x => x.ownerGroupId!),
    }
    return f
  }, [states.filters])

  return (
    <Content 
      pageName="Ownership Statistics" 
      resourceName="Ownership Statistics" 
      breadcrumbs={options?.breadcrumbs}
    >
      <CarryFileCataSearchContext states={states} setStates={setStates} >
        <FileCataAmbientDataContextProvider ownerGroup={true} includePrimaryClassification={true}>
          <Grid container spacing={4}>
            <CarryClassificationRuleProvider>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body1" color="text.primary" mb={1}>Encrypted Files</Typography>
                  <OwnershipPieChart forPlainFiles={false} onDataPointClick={(...rest) => console.log(rest)} filters={searchFilters} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body1" color="text.primary" mb={1}>Plain Files</Typography>
                  <OwnershipPieChart forPlainFiles={true} onDataPointClick={(...rest) => console.log(rest)} filters={searchFilters} />
                </Box>
              </Grid>
            </CarryClassificationRuleProvider>
            <Grid item xs={12}>
              <Card>
                <Stack>
                  <FileCatalogSearchRegion ownerGroup={true} />
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </FileCataAmbientDataContextProvider>
      </CarryFileCataSearchContext>
    </Content>
  );
};

export default OwnershipStatisPage;

const CarryClassificationRuleProvider : FC = ({ children }) => {
  const { classifications } = useFileCataAmbientDataContext()

  return (
    <ClassificationRuleAmbinetDataCotnext.Provider value={{ classifications }} >
      {children}
    </ClassificationRuleAmbinetDataCotnext.Provider>
  )
}

const CarryFileCataSearchContext : FC<{
  states: FileCataSearchStates,
  setStates: Dispatch<React.SetStateAction<FileCataSearchStates>>,
}> = ({ 
  children, states, setStates,
}) => {
  
  const ctrls = useMemo(() => {
    return {
      searchName: (s:string|null) => {
        setStates(v => {
          if (v.filters.name !== s) {
            const v1 = {...v, filters: {...v.filters, name: s}}
            return v1
          } else {
            return v
          }
        })
      },
      addSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.tags.every(x => x.tagId !== t.tagId)) {
            const f1 = {...f, tags: [...f.tags, t]}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.tags.findIndex(x => x.tagId === t.tagId)
          if (i > -1) {
            const tags = [...f.tags]
            tags.splice(i, 1)
            const f1 = {...f, tags}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addPrimaryClassification: (t:PrimaryClassification|null) => {
        setStates(v => {
          const f = v.filters
          if (f.primaryClassification.every(x => x !== t)) {
            const f1 = {...f, primaryClassification: t == null ? [null] : [...f.primaryClassification.filter(x => x != null), t]}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removePrimaryClassification: (t:PrimaryClassification|null) => {
        setStates(v => {
          const f = v.filters
          const i = f.primaryClassification.findIndex(x => x === t)
          if (i > -1) {
            const primaryClassification = [...f.primaryClassification]
            primaryClassification.splice(i, 1)
            const f1 = {...f, primaryClassification}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.secondClassification.every(x => x.secondaryClassificationId !== t.secondaryClassificationId)) {
            const f1 = {...f, secondClassification: [...f.secondClassification, t]}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.secondClassification.findIndex(x => x.secondaryClassificationId === t.secondaryClassificationId)
          if (i > -1) {
            const secondClassification = [...f.secondClassification]
            secondClassification.splice(i, 1)
            const f1 = {...f, secondClassification}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addDocumentType: (dt: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.documentTypes.findIndex(x => x === dt)
          if (i < 0) {
            const f1 = {...f, documentTypes: [...f.documentTypes, dt]}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeDocumentType: (dt: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.documentTypes.findIndex(x => x === dt)
          if (i > -1) {
            const documentTypes = [...f.documentTypes]
            documentTypes.splice(i, 1)
            const f1 = {...f, documentTypes}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addInfoType: (it: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.infoTypes.findIndex(x => x === it)
          if (i < 0) {
            const f1 = {...f, infoTypes: [...f.infoTypes, it]}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeInfoType: (it: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.infoTypes.findIndex(x => x === it)
          if (i > -1) {
            const infoTypes = [...f.infoTypes]
            infoTypes.splice(i, 1)
            const f1 = {...f, infoTypes}
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addFileInstanceOption: (o: FileInstanceOptions) => {
        setStates(v => {
          const f = v.filters
          if (o === f.fileInstanceOptions) {
            return v
          } else {
            const fileInstanceOptions = o
            const f1 = {...f, fileInstanceOptions }
            return {...v, filters: f1}
          }
        })
      },
      removeFileInstanceOption: () => {
        setStates(v => {
          const fileInstanceOptions = FileInstanceOptions.All
          const f = v.filters
          const f1 = {...f, fileInstanceOptions }
          return {...v, filters: f1}
        })
      },
      addOwnerGroup: (o: DataOwnerGroupResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.ownerGroup.every(x => x.ownerGroupId !== o.ownerGroupId)) {
            const f1 = {...f, ownerGroup: [...f.ownerGroup, o]}
            return {...v, filters: f1}
          }
          return v
        })
      },
      removeOwnerGroup: (o: DataOwnerGroupResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.ownerGroup.findIndex(x => x.ownerGroupId === o.ownerGroupId)
          if (i > -1) {
            const ownerGroup = [...f.ownerGroup]
            ownerGroup.splice(i, 1)
            const f1 = {...f, ownerGroup}
            return {...v, filters: f1}
          }
          return v
        })
      },
      switchCurrRes: () => {
        setStates(v => ({...v,
          currRes: v.currRes === osfCatalogRes
            ? pfsCatalogRes
            : osfCatalogRes
        }))
      }
    }
  }, [setStates])
  
  return (
    <FileCataSearchContext.Provider value={{...ctrls, ...states}}>
      {children}
    </FileCataSearchContext.Provider>
  )
}